import {
  withPazireshAvatarThumbParam,
  withoutPazireshAvatarThumbParam,
  isPazireshPicUrl,
  PAZIRESH_PIC_LOAD_TIMEOUT_MS,
} from "./pazireshAvatarUrl";
import { getAllCachedAvatarBlobs, getCachedAvatarBlob, setCachedAvatarBlob } from "./pazireshAvatarCache";

/** Keep below the browser's per-host connection limit (~6). */
const MAX_CONCURRENT_LOADS = 4;

/** @type {Map<string, { status: 'pending', promise: Promise<string|null> } | { status: 'ok', src: string } | { status: 'fail' }>} */
const memoryCache = new Map();

/** @type {Map<string, string>} */
const blobUrlBySource = new Map();

/** @type {Array<{ url: string, resolve: (result: string|null) => void }>} */
const queue = [];
let activeLoads = 0;

function blobUrlFor(url, blob) {
  const existing = blobUrlBySource.get(url);
  if (existing) return existing;
  const blobUrl = URL.createObjectURL(blob);
  blobUrlBySource.set(url, blobUrl);
  return blobUrl;
}

function drainQueue() {
  while (activeLoads < MAX_CONCURRENT_LOADS && queue.length > 0) {
    const job = queue.shift();
    if (!job) break;
    activeLoads += 1;
    runNetworkLoad(job.url)
      .then((result) => {
        job.resolve(result);
      })
      .finally(() => {
        activeLoads -= 1;
        drainQueue();
      });
  }
}

async function fetchAvatarBlob(url, timeoutMs) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    const res = await fetch(url, {
      signal: controller.signal,
      credentials: "omit",
      cache: "force-cache",
    });
    window.clearTimeout(timer);
    if (!res.ok) return null;
    const blob = await res.blob();
    return blob.size > 0 ? blob : null;
  } catch {
    window.clearTimeout(timer);
    return null;
  }
}

function runImageLoad(url) {
  return new Promise((resolve) => {
    const img = new Image();
    let settled = false;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      img.src = "";
      resolve(result);
    };

    const timer = window.setTimeout(() => finish(null), PAZIRESH_PIC_LOAD_TIMEOUT_MS);

    img.onload = () => finish(url);
    img.onerror = () => finish(null);
    img.src = url;
  });
}

async function runNetworkLoad(url) {
  const blob = await fetchAvatarBlob(url, PAZIRESH_PIC_LOAD_TIMEOUT_MS);
  if (blob) {
    await setCachedAvatarBlob(url, blob);
    return blobUrlFor(url, blob);
  }

  return runImageLoad(url);
}

function enqueueNetworkLoad(url) {
  return new Promise((resolve) => {
    queue.push({ url, resolve });
    drainQueue();
  });
}

async function loadFromDiskOrNetwork(url) {
  const cachedBlob = await getCachedAvatarBlob(url);
  if (cachedBlob) {
    return blobUrlFor(url, cachedBlob);
  }
  return enqueueNetworkLoad(url);
}

/**
 * Load a pic.paziresh24.com avatar through local cache + shared network queue.
 * Returns a blob URL (from IndexedDB) or the remote URL on success, or null on failure.
 * Non-paziresh URLs are returned unchanged.
 * @param {string|null|undefined} rawUrl
 * @returns {Promise<string|null>}
 */
export function loadPazireshPicUrl(rawUrl, { skipThumb = false } = {}) {
  if (rawUrl == null || typeof rawUrl !== "string" || !rawUrl.trim()) {
    return Promise.resolve(null);
  }

  const trimmed = rawUrl.trim();
  let url = trimmed;
  if (isPazireshPicUrl(trimmed)) {
    url = skipThumb
      ? withoutPazireshAvatarThumbParam(trimmed)
      : withPazireshAvatarThumbParam(trimmed);
  }
  if (!url || !isPazireshPicUrl(url)) {
    return Promise.resolve(url || trimmed);
  }

  const cached = memoryCache.get(url);
  if (cached?.status === "ok") return Promise.resolve(cached.src);
  if (cached?.status === "fail") return Promise.resolve(null);
  if (cached?.status === "pending") return cached.promise;

  const promise = loadFromDiskOrNetwork(url).then((result) => {
    if (result) {
      memoryCache.set(url, { status: "ok", src: result });
      rememberLoadedAvatar(url);
    } else {
      memoryCache.set(url, { status: "fail" });
    }
    return result;
  });

  memoryCache.set(url, { status: "pending", promise });
  return promise;
}

function normalizedPicUrl(rawUrl, skipThumb = false) {
  if (rawUrl == null || typeof rawUrl !== "string" || !rawUrl.trim()) return null;
  const trimmed = rawUrl.trim();
  if (!isPazireshPicUrl(trimmed)) return null;
  const url = skipThumb ? withoutPazireshAvatarThumbParam(trimmed) : withPazireshAvatarThumbParam(trimmed);
  return url && isPazireshPicUrl(url) ? url : null;
}

/** Already-known result without waiting: the src, null (known to fail), or undefined (not known yet). */
export function peekPazireshPicUrl(rawUrl, { skipThumb = false } = {}) {
  const url = normalizedPicUrl(rawUrl, skipThumb);
  if (!url) return undefined;
  const cached = memoryCache.get(url);
  if (cached?.status === "ok") return cached.src;
  if (cached?.status === "fail") return null;
  return undefined;
}

// URLs that loaded before. The photo service's CORS header is not reliable (the CDN caches one
// origin for everybody), so the blob copy often cannot be saved; the browser's own HTTP cache still
// has the image, and knowing it loaded is enough to draw it at once instead of the letter.
const LOADED_KEY = "hami:avatars-loaded";
const LOADED_MAX = 400;
let loadedUrls = null;
function readLoadedAvatars() {
  if (loadedUrls) return loadedUrls;
  try {
    const list = JSON.parse(localStorage.getItem(LOADED_KEY) || "[]");
    loadedUrls = new Set(Array.isArray(list) ? list : []);
  } catch {
    loadedUrls = new Set();
  }
  return loadedUrls;
}
let saveLoadedTimer = null;
function rememberLoadedAvatar(url) {
  const set = readLoadedAvatars();
  if (set.has(url)) return;
  set.add(url);
  if (saveLoadedTimer) return;
  saveLoadedTimer = setTimeout(() => {
    saveLoadedTimer = null;
    try {
      localStorage.setItem(LOADED_KEY, JSON.stringify([...set].slice(-LOADED_MAX)));
    } catch {
      // storage full: only the next reload's first frame is affected
    }
  }, 1000);
}

let warmPromise = null;
/** Saved avatars into memory (blob URLs, decoded ahead), so a reload draws faces in its first frame. */
export function warmPazireshAvatarCache() {
  if (!warmPromise) {
    for (const url of readLoadedAvatars()) {
      if (!memoryCache.has(url)) memoryCache.set(url, { status: "ok", src: url });
    }
    warmPromise = getAllCachedAvatarBlobs().then((entries) => {
      for (const [url, blob] of entries) {
        if (memoryCache.get(url)?.src?.startsWith?.("blob:")) continue;
        const src = blobUrlFor(url, blob);
        memoryCache.set(url, { status: "ok", src });
        const img = new Image();
        img.src = src;
        img.decode?.().catch(() => {});
      }
    });
  }
  return warmPromise;
}
